/**
 * OrganizationsCard Component
 *
 * Dashboard card for managing organizations and inviting members.
 * Shows user's organizations with quick actions for member management.
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Users,
  UserPlus,
  X,
  Check,
  Loader2,
  Crown,
  Shield,
  Eye,
  RefreshCw,
  Clock,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  useOrganizationStore,
  Organization,
  OrganizationMember,
} from "@/stores/organization/organization-store";
import { api } from "@/services/api/client";
import { ProjectsApi } from "@/services/api/projects";

import * as errorUtils from '@/utils/errors';

interface InviteMemberRequest {
  email: string;
  role: "admin" | "member" | "viewer";
  message?: string;
  homeProjectId?: string;
  homePageId?: string;
}

interface InviteHomepageProject {
  id: string;
  name: string;
  pages: Array<{ id: string; name?: string }>;
}

/**
 * Role icon component
 */
const RoleIcon: React.FC<{ role: string; className?: string }> = ({
  role,
  className = "h-3 w-3",
}) => {
  switch (role) {
    case "owner":
      return <Crown className={className} />;
    case "admin":
      return <Shield className={className} />;
    case "viewer":
      return <Eye className={className} />;
    default:
      return <Users className={className} />;
  }
};

/**
 * Get role badge variant
 */
const getRoleBadgeVariant = (
  role: string
): "default" | "secondary" | "outline" => {
  switch (role) {
    case "owner":
      return "default";
    case "admin":
      return "default";
    case "member":
      return "secondary";
    case "viewer":
      return "outline";
    default:
      return "outline";
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

type InviteRole = "admin" | "member" | "viewer";

const isInviteRole = (value: string): value is InviteRole =>
  value === "admin" || value === "member" || value === "viewer";

const getInvitationRateLimitDetails = (error: unknown) => {
  const data = errorUtils.getApiErrorRecord(error);
  const details = data?.details;

  if (!isRecord(details)) {
    return {
      retryAfterMinutes: 60,
      limitType: "hourly",
      limit: 10,
    };
  }

  const retryAfterMinutes =
    typeof details.retryAfterMinutes === "number"
      ? details.retryAfterMinutes
      : Math.ceil(
          (typeof details.retryAfter === "number" ? details.retryAfter : 3600) /
            60,
        );
  const limitType =
    typeof details.limitType === "string" ? details.limitType : "hourly";
  const limit = typeof details.limit === "number" ? details.limit : 10;

  return { retryAfterMinutes, limitType, limit };
};

/**
 * OrganizationsCard - Main dashboard card component
 */
export const OrganizationsCard: React.FC = () => {
  const { organizations, fetchOrganizations } = useOrganizationStore();
  const [membersOrganization, setMembersOrganization] = useState<Organization | null>(null);

  // Fetch organizations on mount
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Organizations
        </CardTitle>
        <CardDescription>Manage your organizations and team members</CardDescription>
      </CardHeader>
      <CardContent className="flex-1">
        {organizations.length === 0 ? (
          <div className="text-center py-4">
            <Building2 className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              You're not part of any organizations yet
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {organizations.slice(0, 5).map((org) => (
              <OrganizationRow
                key={org._id}
                organization={org}
                onManageMembers={() => setMembersOrganization(org)}
              />
            ))}
            {organizations.length > 5 && (
              <p className="text-xs text-muted-foreground text-center pt-2">
                And {organizations.length - 5} more...
              </p>
            )}
          </div>
        )}
      </CardContent>
      {membersOrganization && (
        <MembersDialog
          organization={membersOrganization}
          open={Boolean(membersOrganization)}
          onOpenChange={(open) => {
            if (!open) {
              setMembersOrganization(null);
            }
          }}
        />
      )}
    </Card>
  );
};

/**
 * OrganizationRow - Individual organization row (clickable to navigate)
 */
interface OrganizationRowProps {
  organization: Organization;
  onManageMembers: () => void;
}

const OrganizationRow: React.FC<OrganizationRowProps> = ({
  organization,
  onManageMembers,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/orgs/${organization.slug}/projects`);
  };

  return (
    <div className="w-full flex items-center justify-between rounded-lg border border-border hover:bg-muted/50 hover:border-primary/30 transition-colors group">
      <button
        onClick={handleClick}
        className="min-w-0 flex-1 flex items-center gap-3 p-3 text-left"
      >
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: 'hsl(var(--primary) / 0.1)',
            color: 'hsl(var(--primary))',
          }}
        >
          <Building2 className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{organization.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge
              variant={getRoleBadgeVariant(organization.membership?.role || "")}
              className="text-[10px] px-1.5 py-0 h-4 capitalize"
            >
              <RoleIcon
                role={organization.membership?.role || ""}
                className="h-2.5 w-2.5 mr-1"
              />
              {organization.membership?.role}
            </Badge>
          </div>
        </div>
      </button>

      <div className="flex items-center gap-1 pr-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            onManageMembers();
          }}
          className="h-8 w-8 p-0"
          title={`Manage members for ${organization.name}`}
          aria-label={`Manage members for ${organization.name}`}
        >
          <Users className="h-4 w-4" />
        </Button>
        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      </div>
    </div>
  );
};

/**
 * MembersDialog - Dialog for viewing and managing members
 */
interface MembersDialogProps {
  organization: Organization;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_MESSAGE_LENGTH = 500;

const MembersDialog: React.FC<MembersDialogProps> = ({
  organization,
  open,
  onOpenChange,
}) => {
  const { resendInvitation, cancelInvitation } = useOrganizationStore();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<InviteRole>("member");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteHomeProjectId, setInviteHomeProjectId] = useState("");
  const [inviteHomePageId, setInviteHomePageId] = useState("");
  const [availableProjects, setAvailableProjects] = useState<InviteHomepageProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsLoadError, setProjectsLoadError] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [memberToCancel, setMemberToCancel] = useState<OrganizationMember | null>(null);

  // Rate limit state (Task 5.3)
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    isLimited: boolean;
    retryAfterMinutes: number;
    message: string;
  } | null>(null);

  // Fetch members when dialog opens
  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<OrganizationMember[]>(
        `/organizations/${organization.slug}/members`
      );
      setMembers(response.data || []);
    } catch {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [organization.slug]);

  useEffect(() => {
    if (open) {
      fetchMembers();
    }
  }, [open, fetchMembers]);

  // Clear rate limit info when dialog closes (Task 5.3)
  useEffect(() => {
    if (!open) {
      setRateLimitInfo(null);
      setShowInviteForm(false);
      setInviteHomeProjectId("");
      setInviteHomePageId("");
    }
  }, [open]);

  const loadHomepageProjects = useCallback(async () => {
    setLoadingProjects(true);
    setProjectsLoadError(false);

    try {
      const response = await ProjectsApi.getProjects(
        { limit: 100 },
        { type: "organization", orgSlug: organization.slug }
      );

      setAvailableProjects(
        response.data?.projects
          ?.map((project) => {
            const fallbackId = isRecord(project) && typeof project._id === "string"
              ? project._id
              : "";
            return {
              id: project.id || fallbackId,
              name: project.name,
              pages:
                project.pages?.map((page) => ({
                  id: page.id,
                  name: page.name,
                })).filter((page) => Boolean(page.id)) ?? [],
            };
          })
          .filter((project) => Boolean(project.id)) ?? []
      );
    } catch (error) {
      console.error("[MembersDialog] Failed to load homepage options:", error);
      setProjectsLoadError(true);
    } finally {
      setLoadingProjects(false);
    }
  }, [organization.slug]);

  const handleToggleInviteForm = async () => {
    const next = !showInviteForm;
    setShowInviteForm(next);

    if (!next) {
      setInviteHomeProjectId("");
      setInviteHomePageId("");
      return;
    }

    if (availableProjects.length === 0) {
      await loadHomepageProjects();
    }
  };

  // Auto-clear rate limit info after the retry time expires (Task 5.3)
  useEffect(() => {
    if (!rateLimitInfo?.isLimited || rateLimitInfo.retryAfterMinutes <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setRateLimitInfo(null);
    }, rateLimitInfo.retryAfterMinutes * 60 * 1000);

    return () => clearTimeout(timer);
  }, [rateLimitInfo]);

  // Handle invite
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      const body: InviteMemberRequest = {
        email: inviteEmail.trim(),
        role: inviteRole,
      };
      if (inviteMessage.trim()) body.message = inviteMessage.trim();
      if (inviteHomeProjectId) body.homeProjectId = inviteHomeProjectId;
      if (inviteHomePageId) body.homePageId = inviteHomePageId;

      const response = await api.post<{ emailSent: boolean; email: string }, InviteMemberRequest>(
        `/organizations/${organization.slug}/members`,
        body
      );

      // Show email delivery status in toast (Task 3.5)
      const emailSent = response.data?.emailSent ?? true;
      if (emailSent) {
        toast.success(`Invitation sent to ${inviteEmail}`, {
          description: "They will receive an email with a link to join.",
        });
      } else {
        toast.success(`Invitation created for ${inviteEmail}`, {
          description: "Email delivery is pending. The invitation is active.",
          icon: <Clock className="h-4 w-4" />,
        });
      }

      // Reset form
      setInviteEmail("");
      setInviteRole("member");
      setInviteMessage("");
      setInviteHomeProjectId("");
      setInviteHomePageId("");
      setShowInviteForm(false);

      // Refresh members list
      await fetchMembers();

      // Clear any rate limit info on success
      setRateLimitInfo(null);
    } catch (error: unknown) {
      // Handle rate limit errors (Task 5.3)
      if (errorUtils.hasErrorCode(error, "INVITATION_RATE_LIMIT_EXCEEDED")) {
        const { retryAfterMinutes, limitType, limit } =
          getInvitationRateLimitDetails(error);

        setRateLimitInfo({
          isLimited: true,
          retryAfterMinutes,
          message: `${limitType.charAt(0).toUpperCase() + limitType.slice(1)} limit of ${limit} invitations reached`,
        });

        toast.error("Invitation limit reached", {
          description: `Try again in ${retryAfterMinutes} minute${retryAfterMinutes !== 1 ? "s" : ""}`,
          icon: <Clock className="h-4 w-4" />,
        });
      } else {
        toast.error("Failed to send invitation", {
          description: errorUtils.getApiErrorMessage(error, "Please try again"),
        });
      }
    } finally {
      setInviting(false);
    }
  };

  // Handle remove member
  const handleRemoveMember = async (member: OrganizationMember) => {
    if (
      !window.confirm(
        `Are you sure you want to remove ${member.name} from ${organization.name}?`
      )
    ) {
      return;
    }

    // Use membershipId if available, fallback to userId for backwards compatibility
    const memberId = member.membershipId || member.userId;

    try {
      await api.delete(
        `/organizations/${organization.slug}/members/${memberId}`
      );
      toast.success(`${member.name} has been removed`);
      await fetchMembers();
    } catch (error: unknown) {
      toast.error("Failed to remove member", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    }
  };

  // Handle resend invitation (Task 3.3)
  const handleResendInvitation = async (member: OrganizationMember) => {
    if (!member.membershipId) {
      toast.error("Cannot resend invitation", {
        description: "Missing membership ID",
      });
      return;
    }

    setResendingId(member.membershipId);
    try {
      const result = await resendInvitation(organization.slug, member.membershipId);

      if (result.emailSent) {
        toast.success(`Invitation resent to ${member.email}`, {
          description: `Resend ${result.resendCount}/${result.maxResends}. Expires in 7 days.`,
        });
      } else {
        toast.success(`Invitation updated for ${member.email}`, {
          description: "Email delivery pending. New expiry set.",
          icon: <Clock className="h-4 w-4" />,
        });
      }

      // Refresh members list
      await fetchMembers();

      // Clear any rate limit info on success
      setRateLimitInfo(null);
    } catch (error: unknown) {
      // Handle rate limit errors (Task 5.3)
      if (errorUtils.hasErrorCode(error, "INVITATION_RATE_LIMIT_EXCEEDED")) {
        const { retryAfterMinutes, limitType, limit } =
          getInvitationRateLimitDetails(error);

        setRateLimitInfo({
          isLimited: true,
          retryAfterMinutes,
          message: `${limitType.charAt(0).toUpperCase() + limitType.slice(1)} limit of ${limit} invitations reached`,
        });

        toast.error("Invitation limit reached", {
          description: `Try again in ${retryAfterMinutes} minute${retryAfterMinutes !== 1 ? "s" : ""}`,
          icon: <Clock className="h-4 w-4" />,
        });
      } else {
        toast.error("Failed to resend invitation", {
          description: errorUtils.getApiErrorMessage(error, "Please try again"),
        });
      }
    } finally {
      setResendingId(null);
    }
  };

  // Handle cancel invitation (Task 3.4)
  const handleCancelInvitation = async () => {
    if (!memberToCancel?.membershipId) {
      toast.error("Cannot cancel invitation");
      return;
    }

    try {
      await cancelInvitation(organization.slug, memberToCancel.membershipId);

      toast.success(`Invitation cancelled`, {
        description: `${memberToCancel.email} will no longer be able to join.`,
      });

      // Refresh members list
      await fetchMembers();
    } catch (error: unknown) {
      toast.error("Failed to cancel invitation", {
        description: errorUtils.getApiErrorMessage(error, "Please try again"),
      });
    } finally {
      setCancelDialogOpen(false);
      setMemberToCancel(null);
    }
  };

  // Open cancel confirmation dialog
  const openCancelDialog = (member: OrganizationMember) => {
    setMemberToCancel(member);
    setCancelDialogOpen(true);
  };

  // Separate active and pending members
  const activeMembers = members.filter(
    (m) => m.status === "active" || !m.status
  );
  const pendingMembers = members.filter(
    (m) => m.status === "invited" || m.status === "pending"
  );
  const selectedHomepageProject = availableProjects.find(
    (project) => project.id === inviteHomeProjectId
  );
  const selectedHomepagePages = selectedHomepageProject?.pages ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {organization.name}
            </DialogTitle>
            <DialogDescription>
              Manage team members and send invitations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Invite Form Toggle */}
            {!showInviteForm ? (
              <Button
                onClick={handleToggleInviteForm}
                className="w-full"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            ) : (
              <form
                onSubmit={handleInvite}
                className="p-4 rounded-lg border border-border bg-muted/30 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">Send Invitation</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleToggleInviteForm}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invite-email" className="text-xs">
                    Email Address
                  </Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    className="h-9"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invite-role" className="text-xs">
                    Role
                  </Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(value) => {
                      if (isInviteRole(value)) setInviteRole(value);
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="h-3 w-3" />
                          Admin
                        </div>
                      </SelectItem>
                      <SelectItem value="member">
                        <div className="flex items-center gap-2">
                          <Users className="h-3 w-3" />
                          Member
                        </div>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="h-3 w-3" />
                          Viewer
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Task 3.1: Message Field with Character Counter */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="invite-message" className="text-xs">
                      Personal Message (optional)
                    </Label>
                    <span
                      className={`text-xs ${
                        inviteMessage.length > MAX_MESSAGE_LENGTH - 50
                          ? inviteMessage.length >= MAX_MESSAGE_LENGTH
                            ? "text-destructive"
                            : "text-[hsl(var(--feedback-warning))]"
                          : "text-muted-foreground"
                      }`}
                    >
                      {inviteMessage.length}/{MAX_MESSAGE_LENGTH}
                    </span>
                  </div>
                  <Textarea
                    id="invite-message"
                    placeholder="Welcome to the team! I'm excited to have you join us..."
                    value={inviteMessage}
                    onChange={(e) => {
                      if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                        setInviteMessage(e.target.value);
                      }
                    }}
                    className="min-h-[80px] resize-none"
                    maxLength={MAX_MESSAGE_LENGTH}
                  />
                </div>

                <div className="space-y-2">
                  <div>
                    <Label htmlFor="invite-home-project" className="text-xs">
                      Pre-assign Homepage (optional)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      New members will land here after accepting if they do not already have a homepage.
                    </p>
                  </div>

                  <Select
                    value={inviteHomeProjectId || "__none__"}
                    onValueChange={(value) => {
                      setInviteHomeProjectId(value === "__none__" ? "" : value);
                      setInviteHomePageId("");
                    }}
                    disabled={loadingProjects || projectsLoadError || availableProjects.length === 0}
                  >
                    <SelectTrigger id="invite-home-project" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No homepage pre-assignment</SelectItem>
                      {availableProjects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {loadingProjects && (
                    <p className="text-xs text-muted-foreground">
                      Loading organization projects...
                    </p>
                  )}
                  {projectsLoadError && (
                    <p className="text-xs text-muted-foreground">
                      Homepage options could not be loaded. You can still send the invite.
                    </p>
                  )}
                  {!loadingProjects && !projectsLoadError && availableProjects.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No organization projects available.
                    </p>
                  )}

                  {inviteHomeProjectId && selectedHomepagePages.length > 0 && (
                    <Select
                      value={inviteHomePageId || "__default__"}
                      onValueChange={(value) =>
                        setInviteHomePageId(value === "__default__" ? "" : value)
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Default page</SelectItem>
                        {selectedHomepagePages.map((page) => (
                          <SelectItem key={page.id} value={page.id}>
                            {page.name || page.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {inviteHomeProjectId && selectedHomepagePages.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Selected project has no page options. Project-only assignment will be used.
                    </p>
                  )}
                </div>

                {/* Rate limit warning (Task 5.3) */}
                {rateLimitInfo?.isLimited && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-[hsl(var(--feedback-warning-bg))] border border-[hsl(var(--feedback-warning)/0.3)] text-[hsl(var(--feedback-warning))]">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                      <p className="font-medium">{rateLimitInfo.message}</p>
                      <p className="mt-0.5 text-[hsl(var(--feedback-warning))] opacity-80">
                        Try again in {rateLimitInfo.retryAfterMinutes} minute{rateLimitInfo.retryAfterMinutes !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={inviting || !inviteEmail.trim() || rateLimitInfo?.isLimited}
                  className="w-full h-9"
                  style={{
                    backgroundColor: rateLimitInfo?.isLimited ? undefined : 'hsl(var(--primary))',
                    color: rateLimitInfo?.isLimited ? undefined : 'hsl(var(--primary-foreground))',
                  }}
                >
                  {inviting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : rateLimitInfo?.isLimited ? (
                    <>
                      <Clock className="h-4 w-4 mr-2" />
                      Rate Limited
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* Members List */}
            <div className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Active Members */}
                  {activeMembers.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                        Active Members ({activeMembers.length})
                      </h4>
                      <div className="space-y-2">
                        {activeMembers.map((member) => (
                          <MemberRow
                            key={member.userId}
                            member={member}
                            onRemove={() => handleRemoveMember(member)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending Invitations - Task 3.2 */}
                  {pendingMembers.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                        Pending Invitations ({pendingMembers.length})
                      </h4>
                      <div className="space-y-2">
                        {pendingMembers.map((member) => (
                          <PendingMemberRow
                            key={member.userId}
                            member={member}
                            onResend={() => handleResendInvitation(member)}
                            onCancel={() => openCancelDialog(member)}
                            isResending={resendingId === member.membershipId}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {members.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No members yet</p>
                      <p className="text-xs">Invite someone to get started</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Invitation Confirmation Dialog - Task 3.4 */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setCancelDialogOpen(false)}
          />
          {/* Dialog */}
          <div className="relative z-10 w-full max-w-md mx-4 bg-background border rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Cancel Invitation?</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to cancel the invitation to{" "}
              <strong>{memberToCancel?.email}</strong>? They will no longer be
              able to join {organization.name} using this invitation link.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(false)}
              >
                Keep Invitation
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelInvitation}
              >
                Cancel Invitation
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * MemberRow - Individual active member row
 */
interface MemberRowProps {
  member: OrganizationMember;
  onRemove: () => void;
}

const MemberRow: React.FC<MemberRowProps> = ({ member, onRemove }) => {
  return (
    <div className="flex items-center justify-between p-2 rounded-lg border border-border">
      <div className="flex items-center gap-2">
        {/* Avatar */}
        {member.avatar ? (
          <img
            src={member.avatar}
            alt={member.name}
            className="h-8 w-8 rounded-full"
          />
        ) : (
          <div
            className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium"
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.1)',
              color: 'hsl(var(--primary))',
            }}
          >
            {member.name.charAt(0).toUpperCase()}
          </div>
        )}

        {/* Info */}
        <div>
          <p className="text-sm font-medium">{member.name}</p>
          <p className="text-xs text-muted-foreground">@{member.username}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Badge
          variant={getRoleBadgeVariant(member.role)}
          className="text-[10px] px-1.5 py-0 h-4 capitalize"
        >
          {member.role}
        </Badge>

        {member.role !== "owner" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * PendingMemberRow - Pending invitation row with resend/cancel buttons
 * Task 3.2, 3.3, 3.4
 */
interface PendingMemberRowProps {
  member: OrganizationMember;
  onResend: () => void;
  onCancel: () => void;
  isResending: boolean;
}

const PendingMemberRow: React.FC<PendingMemberRowProps> = ({
  member,
  onResend,
  onCancel,
  isResending,
}) => {
  // Calculate expiry info
  const expiryInfo = useMemo(() => {
    if (!member.expiresAt) return null;

    const expiresAt = new Date(member.expiresAt);
    const now = new Date();
    const hoursRemaining = Math.floor(
      (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60)
    );
    const daysRemaining = Math.floor(hoursRemaining / 24);

    const isExpiringSoon = hoursRemaining < 24 && hoursRemaining > 0;
    const isExpired = hoursRemaining <= 0;

    let expiryText = "";
    if (isExpired) {
      expiryText = "Expired";
    } else if (hoursRemaining < 1) {
      expiryText = "Expires in less than 1 hour";
    } else if (hoursRemaining < 24) {
      expiryText = `Expires in ${hoursRemaining} hour${hoursRemaining !== 1 ? "s" : ""}`;
    } else {
      expiryText = `Expires in ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""}`;
    }

    return { isExpiringSoon, isExpired, expiryText, hoursRemaining };
  }, [member.expiresAt]);

  // Format invited date
  const invitedDateText = useMemo(() => {
    if (!member.invitedAt) return null;
    const date = new Date(member.invitedAt);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }, [member.invitedAt]);

  // Check if max resends reached
  const maxResendsReached =
    member.resendCount !== undefined &&
    member.maxResends !== undefined &&
    member.resendCount >= member.maxResends;

  return (
    <div
      className={`p-3 rounded-lg border border-dashed ${
        expiryInfo?.isExpired
          ? "border-destructive/50 bg-destructive/5"
          : expiryInfo?.isExpiringSoon
            ? "border-[hsl(var(--feedback-warning)/0.5)] bg-[hsl(var(--feedback-warning-bg)/0.5)]"
            : "border-[hsl(var(--feedback-warning)/0.4)] bg-[hsl(var(--feedback-warning-bg)/0.3)]"
      }`}
    >
      {/* Top row: Avatar, name, role */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Avatar */}
          {member.avatar ? (
            <img
              src={member.avatar}
              alt={member.name}
              className="h-8 w-8 rounded-full opacity-70"
            />
          ) : (
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium opacity-70"
              style={{
                backgroundColor: 'hsl(var(--primary) / 0.1)',
                color: 'hsl(var(--primary))',
              }}
            >
              {member.name.charAt(0).toUpperCase()}
            </div>
          )}

          {/* Info */}
          <div>
            <p className="text-sm font-medium">{member.name || member.email}</p>
            <p className="text-xs text-muted-foreground">{member.email}</p>
          </div>
        </div>

        <Badge
          variant={getRoleBadgeVariant(member.role)}
          className="text-[10px] px-1.5 py-0 h-4 capitalize"
        >
          {member.role}
        </Badge>
      </div>

      {/* Middle row: Invitation status info */}
      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
        {invitedDateText && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Invited {invitedDateText}
          </span>
        )}
        {expiryInfo && (
          <span
            className={`flex items-center gap-1 ${
              expiryInfo.isExpired
                ? "text-destructive"
                : expiryInfo.isExpiringSoon
                  ? "text-[hsl(var(--feedback-warning))]"
                  : ""
            }`}
          >
            {(expiryInfo.isExpired || expiryInfo.isExpiringSoon) && (
              <AlertCircle className="h-3 w-3" />
            )}
            {expiryInfo.expiryText}
          </span>
        )}
      </div>

      {/* Bottom row: Action buttons */}
      <div className="flex items-center justify-end gap-2 mt-3">
        {/* Resend button - Task 3.3 */}
        <Button
          variant="outline"
          size="sm"
          onClick={onResend}
          disabled={isResending || maxResendsReached}
          className="h-7 text-xs"
          title={
            maxResendsReached
              ? "Maximum resends reached"
              : `Resend invitation (${member.resendCount || 0}/${member.maxResends || 3})`
          }
        >
          {isResending ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3 mr-1" />
          )}
          Resend{" "}
          {member.maxResends !== undefined &&
            `(${member.resendCount || 0}/${member.maxResends})`}
        </Button>

        {/* Cancel button - Task 3.4 */}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
          className="h-7 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        >
          <X className="h-3 w-3 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default OrganizationsCard;
